/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Android per-instance configuration of the insight widgets. Each placed widget
 * (identified by its launcher `widgetId`) stores `{ kind, config, updatedAt }`
 * under one AsyncStorage key; the headless task handler, the configuration
 * screen and the snapshot builder (period union) all read it.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

import { WIDGET_INSTANCE_CONFIG_KEY } from '../../config/widgetConfig';

/**
 * Read the whole map. Never throws.
 * @returns {Promise<Record<string, { kind: string, config: object, updatedAt: number }>>}
 */
const readAll = async () => {
    try {
        const raw = await AsyncStorage.getItem(WIDGET_INSTANCE_CONFIG_KEY);
        const parsed = raw ? JSON.parse(raw) : null;

        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (_error) {
        return {};
    }
};

/**
 * Persist the whole map. Never throws.
 * @param {object} map
 * @returns {Promise<void>}
 */
const writeAll = async (map) => {
    try {
        await AsyncStorage.setItem(WIDGET_INSTANCE_CONFIG_KEY, JSON.stringify(map));
    } catch (_error) {
        // Best-effort: a failed write keeps the previous config.
    }
};

/**
 * Get one instance's stored entry.
 * @param {number|string} widgetId
 * @returns {Promise<{ kind: string, config: object, updatedAt: number }|null>}
 */
export const getInstanceConfig = async (widgetId) => {
    if (widgetId === undefined || widgetId === null) {
        return null;
    }
    const map = await readAll();

    return map[String(widgetId)] || null;
};

/**
 * Store (replace) one instance's config.
 * @param {number|string} widgetId
 * @param {string} kind Widget kind id.
 * @param {object} config Instance config (validated at resolve time).
 * @returns {Promise<void>}
 */
export const setInstanceConfig = async (widgetId, kind, config) => {
    if (widgetId === undefined || widgetId === null) {
        return;
    }
    const map = await readAll();
    map[String(widgetId)] = { kind, config: config || {}, updatedAt: Date.now() };
    await writeAll(map);
};

/**
 * Remove one instance's config (WIDGET_DELETED).
 * @param {number|string} widgetId
 * @returns {Promise<void>}
 */
export const removeInstanceConfig = async (widgetId) => {
    const map = await readAll();
    if (map[String(widgetId)] !== undefined) {
        delete map[String(widgetId)];
        await writeAll(map);
    }
};

/**
 * List every stored instance entry.
 * @returns {Promise<Array<{ widgetId: string, kind: string, config: object, updatedAt: number }>>}
 */
export const listInstanceConfigs = async () => {
    const map = await readAll();

    return Object.keys(map).map((widgetId) => ({ widgetId, ...map[widgetId] }));
};
